
import React, { useState } from 'react';
import { Store, StoreKYC } from '../types';
import { storage } from '../lib/storage';
import { Building, ShieldCheck, MapPin, CreditCard, ArrowRight, Loader2, CheckCircle, FileText, Upload } from 'lucide-react';

interface MerchantOnboardingProps {
  store: Store;
  onComplete: () => void;
}

const MerchantOnboarding: React.FC<MerchantOnboardingProps> = ({ store, onComplete }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [kycData, setKycData] = useState<Partial<StoreKYC>>(store.kyc || {
    businessType: 'INDIVIDUAL',
    fullName: '',
  });
  const [taxData, setTaxData] = useState({
    taxNumber: store.taxConfig?.taxNumber || '',
    address: store.taxConfig?.businessAddress || '',
  });

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleSubmit = async () => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const updatedStore: Store = {
      ...store,
      taxConfig: {
        ...store.taxConfig,
        enabled: true,
        taxNumber: taxData.taxNumber,
        businessAddress: taxData.address,
        taxRate: 18 // Default
      },
      kyc: {
        ...kycData,
        submittedAt: new Date().toISOString()
      } as StoreKYC
    };

    storage.saveStoreConfig(updatedStore);
    setIsLoading(false);
    onComplete();
  };

  return (
    <div className="max-w-3xl mx-auto py-12 px-4 animate-in fade-in duration-700">
      <div className="bg-white rounded-[40px] shadow-2xl border border-gray-100 overflow-hidden">
        {/* Progress Bar */}
        <div className="h-2 bg-gray-100 flex">
           {[1, 2, 3].map((s) => (
             <div 
               key={s} 
               className={`h-full transition-all duration-500 ${s <= step ? 'bg-blue-600' : 'bg-transparent'}`} 
               style={{ width: '33.33%' }} 
             />
           ))}
        </div>

        <div className="p-10 sm:p-16 space-y-10">
          <div className="text-center space-y-2">
             <h2 className="text-3xl font-black text-gray-900 tracking-tight">Setup Your Business</h2>
             <p className="text-gray-400 font-medium">Verify your identity to start selling on Lynqar.</p>
          </div>

          {step === 1 && (
            <div className="space-y-8 animate-in slide-in-from-right-10">
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <button 
                    onClick={() => setKycData({...kycData, businessType: 'INDIVIDUAL'})}
                    className={`p-8 rounded-[2rem] border-2 text-left transition-all ${kycData.businessType === 'INDIVIDUAL' ? 'border-blue-600 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                     <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-4 text-blue-600">
                        <CheckCircle className="h-6 w-6" />
                     </div>
                     <h4 className="font-black text-gray-900">Individual</h4>
                     <p className="text-xs text-gray-500 mt-1">Selling as a freelancer or sole person.</p>
                  </button>
                  <button 
                    onClick={() => setKycData({...kycData, businessType: 'COMPANY'})}
                    className={`p-8 rounded-[2rem] border-2 text-left transition-all ${kycData.businessType === 'COMPANY' ? 'border-blue-600 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                     <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-4 text-blue-600">
                        <Building className="h-6 w-6" />
                     </div>
                     <h4 className="font-black text-gray-900">Registered Company</h4>
                     <p className="text-xs text-gray-500 mt-1">Selling as a legal business entity.</p>
                  </button>
               </div>
               <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Full Legal Name</label>
                  <input 
                    type="text" 
                    value={kycData.fullName} 
                    onChange={e => setKycData({...kycData, fullName: e.target.value})}
                    placeholder="As per Government ID"
                    className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none font-bold focus:ring-2 focus:ring-blue-500" 
                  />
               </div>
               <button onClick={handleNext} disabled={!kycData.fullName} className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black text-lg shadow-xl shadow-blue-100 flex items-center justify-center gap-2 hover:bg-blue-700 transition-all disabled:opacity-50">
                  Next Step <ArrowRight className="h-5 w-5" />
               </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8 animate-in slide-in-from-right-10">
               <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tax Registration ID (GSTIN/VAT)</label>
                    <input 
                      type="text" 
                      value={taxData.taxNumber} 
                      onChange={e => setTaxData({...taxData, taxNumber: e.target.value})}
                      placeholder="e.g. 27AAAAA0000A1Z5"
                      className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none font-bold focus:ring-2 focus:ring-blue-500" 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Business Headquarters Address</label>
                    <textarea 
                      rows={3}
                      value={taxData.address} 
                      onChange={e => setTaxData({...taxData, address: e.target.value})}
                      placeholder="Full street address, city, and zip..."
                      className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none font-bold focus:ring-2 focus:ring-blue-500 resize-none" 
                    />
                  </div>
               </div>
               <div className="flex gap-4">
                  <button onClick={handleBack} className="flex-1 bg-gray-100 text-gray-600 py-5 rounded-2xl font-black">Back</button>
                  <button onClick={handleNext} disabled={!taxData.taxNumber || !taxData.address} className="flex-[2] bg-blue-600 text-white py-5 rounded-2xl font-black text-lg shadow-xl shadow-blue-100 flex items-center justify-center gap-2 hover:bg-blue-700 transition-all disabled:opacity-50">
                    Next Step <ArrowRight className="h-5 w-5" />
                  </button>
               </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-8 animate-in slide-in-from-right-10">
               <div className="p-8 bg-blue-50 rounded-[2.5rem] border border-blue-100 flex items-center gap-6">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center shadow-lg shrink-0">
                     <ShieldCheck className="h-8 w-8" />
                  </div>
                  <div className="space-y-1">
                     <h4 className="font-black text-gray-900 leading-tight">Verification in Progress</h4>
                     <p className="text-xs text-blue-600 font-medium">Once you submit, our Super Admins will review your details within 24-48 hours.</p>
                  </div>
               </div>

               <div className="space-y-6">
                  <div className="p-6 border-2 border-dashed border-gray-200 rounded-[2rem] text-center space-y-3 group hover:border-blue-300 transition-all cursor-pointer">
                     <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center mx-auto text-gray-400 group-hover:text-blue-600">
                        <Upload className="h-6 w-6" />
                     </div>
                     <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Upload ID Proof (Aadhar/Passport)</p>
                  </div>
                  <div className="p-6 border-2 border-dashed border-gray-200 rounded-[2rem] text-center space-y-3 group hover:border-blue-300 transition-all cursor-pointer">
                     <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center mx-auto text-gray-400 group-hover:text-blue-600">
                        <Upload className="h-6 w-6" />
                     </div>
                     <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Upload Address Proof (Utility Bill)</p>
                  </div>
               </div>

               <div className="flex gap-4">
                  <button onClick={handleBack} disabled={isLoading} className="flex-1 bg-gray-100 text-gray-600 py-5 rounded-2xl font-black disabled:opacity-50">Back</button>
                  <button onClick={handleSubmit} disabled={isLoading} className="flex-[2] bg-blue-600 text-white py-5 rounded-2xl font-black text-lg shadow-xl shadow-blue-100 flex items-center justify-center gap-2 hover:bg-blue-700 transition-all disabled:opacity-50">
                    {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : "Complete & Submit"}
                  </button>
               </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MerchantOnboarding;
